/**
 * DCMCloud — Teaching File Overlay Panel
 * Designed for radiology educators and residents.
 * - "Quiz mode": hide findings, let learner annotate, then reveal the answer
 * - Scoring per session (correct / incorrect / skipped)
 * - Case notes visible to instructors; hidden until revealed in quiz mode
 */
import React, { useState, useCallback } from 'react';

interface TeachingCase {
  id: string;
  title: string;
  modality: string;
  bodyPart: string;
  diagnosis: string;
  teachingPoints: string;
  difficulty: 'easy' | 'medium' | 'hard';
  tags: string[];
}

const DEMO_CASES: TeachingCase[] = [
  {
    id: 'tc-001',
    title: 'Pulmonary Embolism',
    modality: 'CT',
    bodyPart: 'Chest',
    diagnosis: 'Bilateral pulmonary embolism with saddle embolus at the main pulmonary artery bifurcation.',
    teachingPoints: 'Look for filling defects in the pulmonary arteries. Assess RV:LV ratio >1 as indicator of strain. Check for peripheral wedge-shaped consolidation (Hampton hump).',
    difficulty: 'medium',
    tags: ['chest', 'vascular', 'emergency'],
  },
  {
    id: 'tc-002',
    title: 'Liver Cirrhosis',
    modality: 'MR',
    bodyPart: 'Abdomen',
    diagnosis: 'Advanced liver cirrhosis with nodular surface, right lobe atrophy, caudate lobe hypertrophy, and moderate ascites.',
    teachingPoints: 'Caudate lobe hypertrophy (caudate:right lobe ratio >0.65) is pathognomonic. Check for varices, splenomegaly, and hepatocellular carcinoma.',
    difficulty: 'medium',
    tags: ['abdomen', 'liver', 'HCC surveillance'],
  },
  {
    id: 'tc-003',
    title: 'Periapical Abscess',
    modality: 'CT',
    bodyPart: 'Dental',
    diagnosis: 'Periapical abscess at tooth 36 with cortical bone erosion and early soft tissue extension.',
    teachingPoints: 'Periapical lucency on CBCT indicates pulp necrosis. Measure bone loss. Assess proximity to inferior alveolar canal.',
    difficulty: 'easy',
    tags: ['dental', 'cbct', 'infection'],
  },
  {
    id: 'tc-004',
    title: 'Brain Metastases',
    modality: 'MR',
    bodyPart: 'Brain',
    diagnosis: 'Multiple brain metastases at the grey-white matter junction with ring enhancement on post-contrast T1.',
    teachingPoints: 'Grey-white junction location is characteristic of haematogenous metastases. Look for vasogenic oedema. Contrast-enhancing lesions suggest blood-brain barrier breakdown.',
    difficulty: 'hard',
    tags: ['brain', 'oncology', 'post-contrast'],
  },
];

const DIFF_COLORS = { easy: '#10b981', medium: '#f59e0b', hard: '#ef4444' };

interface Score { correct: number; incorrect: number; skipped: number; }

export function TeachingFileOverlay() {
  const [cases] = useState<TeachingCase[]>(DEMO_CASES);
  const [activeCaseId, setActiveCaseId] = useState<string | null>(null);
  const [quizMode, setQuizMode] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState<Score>({ correct: 0, incorrect: 0, skipped: 0 });
  const [filterTag, setFilterTag] = useState('');

  const activeCase = cases.find(c => c.id === activeCaseId);
  const allTags = Array.from(new Set(cases.flatMap(c => c.tags))).sort();
  const filtered = filterTag ? cases.filter(c => c.tags.includes(filterTag)) : cases;

  const openCase = useCallback((id: string) => {
    setActiveCaseId(id);
    setRevealed(false);
    setUserAnswer('');
  }, []);

  const reveal = () => setRevealed(true);

  const markAnswer = (outcome: keyof Score) => {
    setScore(prev => ({ ...prev, [outcome]: prev[outcome] + 1 }));
    // Advance to next case
    const idx = filtered.findIndex(c => c.id === activeCaseId);
    const next = filtered[idx + 1];
    if (next) openCase(next.id);
    else setActiveCaseId(null);
  };

  const totalAnswered = score.correct + score.incorrect + score.skipped;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#1e1e2e', color: '#e2e8f0', fontFamily: 'sans-serif', fontSize: '12px', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600, fontSize: '13px', color: '#8b5cf6' }}>Teaching File</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>Quiz</span>
          <button
            onClick={() => { setQuizMode(q => !q); setRevealed(false); setUserAnswer(''); }}
            style={{ width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer', background: quizMode ? '#8b5cf6' : '#374151', position: 'relative', transition: 'background 0.2s' }}
          >
            <span style={{ position: 'absolute', top: 2, left: quizMode ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
          </button>
        </div>
      </div>

      {/* Score bar */}
      {quizMode && totalAnswered > 0 && (
        <div style={{ padding: '6px 12px', background: '#111827', display: 'flex', gap: '16px', fontSize: '11px', borderBottom: '1px solid #333' }}>
          <span style={{ color: '#10b981' }}>✓ {score.correct}</span>
          <span style={{ color: '#ef4444' }}>✗ {score.incorrect}</span>
          <span style={{ color: '#9ca3af' }}>— {score.skipped} skipped</span>
          <span style={{ color: '#6b7280', marginLeft: 'auto' }}>{totalAnswered} answered</span>
        </div>
      )}

      {/* Case list (when no active case) */}
      {!activeCase && (
        <>
          {/* Tag filter */}
          <div style={{ padding: '6px 12px', borderBottom: '1px solid #1f2937', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <button key="all" onClick={() => setFilterTag('')} style={{ padding: '2px 8px', borderRadius: '10px', border: `1px solid ${!filterTag ? '#8b5cf6' : '#374151'}`, background: !filterTag ? '#8b5cf622' : 'transparent', color: !filterTag ? '#8b5cf6' : '#9ca3af', cursor: 'pointer', fontSize: '10px' }}>All</button>
            {allTags.map(tag => (
              <button key={tag} onClick={() => setFilterTag(tag)} style={{ padding: '2px 8px', borderRadius: '10px', border: `1px solid ${filterTag === tag ? '#8b5cf6' : '#374151'}`, background: filterTag === tag ? '#8b5cf622' : 'transparent', color: filterTag === tag ? '#8b5cf6' : '#9ca3af', cursor: 'pointer', fontSize: '10px' }}>{tag}</button>
            ))}
          </div>
          {filtered.map(c => (
            <div key={c.id} onClick={() => openCase(c.id)} style={{ padding: '10px 12px', borderBottom: '1px solid #1f2937', cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#111827')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontWeight: 600, flex: 1 }}>{quizMode ? `Case: ${c.modality} — ${c.bodyPart}` : c.title}</span>
                <span style={{ fontSize: '10px', color: DIFF_COLORS[c.difficulty], border: `1px solid ${DIFF_COLORS[c.difficulty]}`, borderRadius: 4, padding: '1px 6px' }}>{c.difficulty}</span>
              </div>
              <div style={{ color: '#6b7280', fontSize: '11px' }}>{c.modality} · {c.bodyPart}</div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                {c.tags.map(t => <span key={t} style={{ fontSize: '10px', color: '#9ca3af', background: '#1f2937', borderRadius: 4, padding: '1px 5px' }}>{t}</span>)}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Active case view */}
      {activeCase && (
        <div style={{ padding: '10px 12px', flex: 1 }}>
          <button onClick={() => setActiveCaseId(null)} style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '11px', marginBottom: '8px', padding: 0 }}>← Back to list</button>
          <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '4px' }}>
            {quizMode ? `${activeCase.modality} — ${activeCase.bodyPart}` : activeCase.title}
          </div>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
            <span style={{ fontSize: '10px', color: '#9ca3af', background: '#1f2937', borderRadius: 4, padding: '1px 6px' }}>{activeCase.modality}</span>
            <span style={{ fontSize: '10px', color: DIFF_COLORS[activeCase.difficulty], border: `1px solid ${DIFF_COLORS[activeCase.difficulty]}`, borderRadius: 4, padding: '1px 6px' }}>{activeCase.difficulty}</span>
          </div>

          {/* Quiz: user answer */}
          {quizMode && !revealed && (
            <>
              <label style={{ display: 'block', color: '#9ca3af', fontSize: '10px', textTransform: 'uppercase', marginBottom: '4px' }}>Your Diagnosis</label>
              <textarea
                rows={3}
                value={userAnswer}
                onChange={e => setUserAnswer(e.target.value)}
                placeholder="Type your diagnosis / key findings…"
                style={{ width: '100%', background: '#111827', color: '#e2e8f0', border: '1px solid #374151', borderRadius: 6, padding: '6px 8px', fontSize: '12px', resize: 'vertical', boxSizing: 'border-box', marginBottom: '8px' }}
              />
              <button onClick={reveal} style={{ width: '100%', padding: '8px', borderRadius: 6, background: '#8b5cf6', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                Reveal Answer
              </button>
            </>
          )}

          {/* Diagnosis — shown always in study mode, revealed in quiz mode */}
          {(!quizMode || revealed) && (
            <>
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Diagnosis</div>
                <div style={{ background: '#111827', borderRadius: 6, padding: '8px', lineHeight: 1.6, color: '#e2e8f0' }}>{activeCase.diagnosis}</div>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>Teaching Points</div>
                <div style={{ background: '#111827', borderRadius: 6, padding: '8px', lineHeight: 1.6, color: '#d1d5db' }}>{activeCase.teachingPoints}</div>
              </div>
            </>
          )}

          {/* Quiz scoring buttons */}
          {quizMode && revealed && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button onClick={() => markAnswer('correct')} style={{ flex: 1, padding: '8px', borderRadius: 6, background: '#10b981', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>✓ Correct</button>
              <button onClick={() => markAnswer('incorrect')} style={{ flex: 1, padding: '8px', borderRadius: 6, background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>✗ Incorrect</button>
              <button onClick={() => markAnswer('skipped')} style={{ padding: '8px 12px', borderRadius: 6, background: 'transparent', color: '#9ca3af', border: '1px solid #374151', cursor: 'pointer' }}>Skip</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TeachingFileOverlay;
